'use client'

/**
 * StockChartTabs — a single chart area on /stocks/[symbol] with an Intraday | Daily toggle, instead of
 * two charts side by side. Both children speak the same visual language (candles + 9/50/200 EMA + volume),
 * so flipping the toggle just changes the time scale. Intraday is the live pulse (price header + 1m→5m
 * candles), Daily is the bigger-picture daily candles with a 3M/6M/1Y range.
 */

import { useState } from 'react'
import { Activity, CalendarDays } from 'lucide-react'
import StockLivePulse, { type InitialQuote } from '@/components/StockLivePulse'
import StockDailyChart from '@/components/StockDailyChart'

type Mode = 'intraday' | 'daily'
const TABS: { key: Mode; label: string; icon: typeof Activity }[] = [
  { key: 'intraday', label: 'Intraday', icon: Activity },
  { key: 'daily', label: 'Daily', icon: CalendarDays },
]

export default function StockChartTabs({
  symbol, initialPrice, initialPrevClose, srLine, initialQuote,
}: { symbol: string; initialPrice: number | null; initialPrevClose: number | null; srLine?: string; initialQuote?: InitialQuote }) {
  const [mode, setMode] = useState<Mode>('intraday')
  return (
    <div>
      <div className="mb-2 inline-flex rounded-xl border border-border bg-muted p-0.5">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setMode(key)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors ${
              mode === key ? 'bg-amber-500 text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            aria-pressed={mode === key}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {mode === 'intraday' ? (
        <div>
          <StockLivePulse symbol={symbol} initialPrice={initialPrice} initialPrevClose={initialPrevClose} initialQuote={initialQuote} />
          {srLine && <p className="sr-only">{srLine}</p>}
        </div>
      ) : (
        <StockDailyChart symbol={symbol} />
      )}
    </div>
  )
}
